import { Link, useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/useI18n';
import { useHasAdminPermission } from '@/stores/authStore';

interface AdminPermissionGateProps {
  /** Permission (`resource:action`) required to see the page. */
  permission: string;
  children: React.ReactNode;
}

/**
 * Renders an admin page only when the signed-in admin holds `permission`.
 * Otherwise shows a "no access" panel instead of letting the page load and
 * fail with 403 errors. The backend still enforces every permission.
 */
export function AdminPermissionGate({ permission, children }: AdminPermissionGateProps) {
  const { t } = useI18n();
  const allowed = useHasAdminPermission(permission);
  const { pathname } = useLocation();

  if (allowed) return <>{children}</>;

  return (
    <div
      role="alert"
      className="mx-auto mt-12 max-w-md rounded-2xl border bg-white px-6 py-10 text-center shadow-sm"
    >
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <ShieldOff className="h-6 w-6" aria-hidden="true" />
      </span>
      <h1 className="mt-4 text-lg font-semibold">{t('admin.noAccess.title')}</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        {t('admin.noAccess.description', { permission })}
      </p>
      {pathname.replace(/\/+$/, '') !== '/admin' && (
        <Button asChild variant="outline" className="mt-5">
          <Link to="/admin">{t('admin.noAccess.back')}</Link>
        </Button>
      )}
    </div>
  );
}
